import LoadError from "../components/LoadError";
import { Link, useSearchParams } from "react-router-dom";
import Dialog from "../components/Dialog";
import { discardChanges } from "../components/ConfirmProvider";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
import useActionLock from "../hooks/useActionLock";
import { formatMoney } from "../utils/format";
import { useConfirm } from "../components/ConfirmProvider";
import BillingSummary from "../components/BillingSummary";
import SourceBillingConditions from "../components/SourceBillingConditions";
import {
  Archive,
  RotateCcw,
  AlertCircle,
  Calendar,
  Edit,
  Pause,
  Play,
  Plus,
  Receipt,
  RefreshCw,
  Trash2,
  User,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

import { abonnementsAPI, clientsAPI } from "../services/api";
import toast from "react-hot-toast";

const PERIODICITES = [
  { value: "mensuel", label: "Mensuel" },
  { value: "trimestriel", label: "Trimestriel" },
  { value: "semestriel", label: "Semestriel" },
  { value: "annuel", label: "Annuel" },
];

const EMPTY_FORM = {
  client_id: "",
  libelle: "",
  description: "",
  montant_ht: "",
  unite: "forfait",
  periodicite: "mensuel",
  date_debut: new Date().toISOString().slice(0, 10),
  date_fin: "",
  actif: true,
  notes: "",
  afficher_offre_annuelle: false,
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fr-FR") : "-";

export const AbonnementForm = ({ abonnement, clients, onSubmit, onCancel }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [baseline, setBaseline] = useState(JSON.stringify(EMPTY_FORM));
  const confirm = useConfirm();
  const [submitBusy, runSubmit] = useActionLock();
  const normalize = (value) => JSON.stringify({ ...value, montant_ht: value.montant_ht === "" ? "" : Number(value.montant_ht) });
  const dirty = normalize(form) !== normalize(JSON.parse(baseline));
  useUnsavedChanges(dirty);
  const close = async () => { if (!saving && !submitBusy && (!dirty || await confirm(discardChanges))) onCancel(); };

  useEffect(() => {
    if (!abonnement) {
      setForm(EMPTY_FORM);
      return;
    }
    const initial = {
      ...EMPTY_FORM,
      ...abonnement,
      description: abonnement.description || "",
      date_fin: abonnement.date_fin || "",
      notes: abonnement.notes || "",
      montant_ht: String(abonnement.montant_ht ?? ""),
    };
    setForm(initial);
    setBaseline(JSON.stringify(initial));
  }, [abonnement]);

  const selectedClient = clients.find((client) => client.id === form.client_id);
  const billingCurrency = abonnement && form.client_id === abonnement.client_id
    ? abonnement.client_devise || abonnement.conditions_facturation?.devise || selectedClient?.devise
    : selectedClient?.devise;

  const setField = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    return runSubmit(async () => {
    if (abonnement && !dirty) return;
    const montant = Number(form.montant_ht);
    if (!Number.isFinite(montant) || montant <= 0) {
      toast.error("Le montant HT doit être supérieur à 0");
      return;
    }

    setSaving(true); setSaveError("");
    try {
      await onSubmit({
        client_id: form.client_id,
        libelle: form.libelle.trim(),
        description: form.description.trim() || null,
        montant_ht: montant,
        unite: form.unite.trim() || "forfait",
        periodicite: form.periodicite,
        date_debut: form.date_debut,
        date_fin: form.date_fin || null,
        actif: form.actif,
        notes: form.notes.trim() || null,
        afficher_offre_annuelle:
          form.periodicite === "mensuel" && form.afficher_offre_annuelle,
      });
    } catch (error) { setSaveError(typeof error?.response?.data?.detail === "string" ? error.response.data.detail : "L’enregistrement a échoué. Votre saisie est conservée.");
    } finally {
      setSaving(false);
    }
    });
  };

  return (
    <Dialog title={abonnement ? "Modifier l'abonnement" : "Nouvel abonnement"} onClose={close} busy={saving || submitBusy} footer={<><button type="button" onClick={close} disabled={saving || submitBusy} className="btn-secondary">Annuler</button><button type="submit" form="subscription-form" className="btn-primary" disabled={saving || submitBusy || (abonnement && !dirty)}>{saving ? "Enregistrement…" : "Enregistrer"}</button></>}>
        <form id="subscription-form" onSubmit={handleSubmit} className="space-y-5">
          {saveError && <p role="alert" className="text-sm text-red-700">{saveError}</p>}
          <fieldset disabled={saving || submitBusy} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="abonnements-4606" className="mb-1 block text-sm font-medium text-gray-700">
                Client *
              </label>
              <select id="abonnements-4606"
                className="input-field"
                required
                value={form.client_id}
                onChange={(event) => {
                  const nextClient = clients.find((client) => client.id === event.target.value);
                  if (billingCurrency && nextClient?.devise !== billingCurrency) setField("montant_ht", "");
                  setField("client_id", event.target.value);
                }}
              >
                <option value="">Sélectionner un client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.nom}
                    {client.societe ? ` - ${client.societe}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="abonnements-5579" className="mb-1 block text-sm font-medium text-gray-700">
                Libellé *
              </label>
              <input id="abonnements-5579"
                type="text"
                className="input-field"
                required
                placeholder="Maintenance site vitrine"
                value={form.libelle}
                onChange={(event) => setField("libelle", event.target.value)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="abonnements-6059" className="mb-1 block text-sm font-medium text-gray-700">
              Description
            </label>
            <input id="abonnements-6059"
              type="text"
              className="input-field"
              placeholder="hébergement, sauvegardes et mises à jour"
              value={form.description}
              onChange={(event) => setField("description", event.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              Reprise dans la ligne de prestation de chaque facture générée.
            </p>
          </div>

          {abonnement && form.client_id === abonnement.client_id
            ? <SourceBillingConditions kind="abonnements" sourceId={abonnement.id} />
            : <BillingSummary clientId={form.client_id} subscription />}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="abonnements-6946" className="mb-1 block text-sm font-medium text-gray-700">
                Montant HT par période ({billingCurrency || "devise du client"}) *
              </label>
              <input id="abonnements-6946"
                type="number"
                step="0.01"
                min="0.01"
                className="input-field"
                required
                value={form.montant_ht}
                onChange={(event) => setField("montant_ht", event.target.value)}
              />
            </div>

            <div>
              <label htmlFor="abonnements-7478" className="mb-1 block text-sm font-medium text-gray-700">
                Périodicité *
              </label>
              <select id="abonnements-7478"
                className="input-field"
                value={form.periodicite}
                onChange={(event) =>
                  setField("periodicite", event.target.value)
                }
              >
                {PERIODICITES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="abonnements-8098" className="mb-1 block text-sm font-medium text-gray-700">
                Unité
              </label>
              <input id="abonnements-8098"
                type="text"
                className="input-field"
                value={form.unite}
                onChange={(event) => setField("unite", event.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="abonnements-8560" className="mb-1 block text-sm font-medium text-gray-700">
                Date de début *
              </label>
              <input id="abonnements-8560"
                type="date"
                className="input-field"
                required
                value={form.date_debut}
                onChange={(event) => setField("date_debut", event.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Sert d'ancre : toutes les périodes sont calculées depuis cette
                date.
              </p>
            </div>

            <div>
              <label htmlFor="abonnements-9161" className="mb-1 block text-sm font-medium text-gray-700">
                Date de fin
              </label>
              <input id="abonnements-9161"
                type="date"
                className="input-field"
                value={form.date_fin}
                onChange={(event) => setField("date_fin", event.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Vide = reconduction sans limite.
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="abonnements-9690" className="mb-1 block text-sm font-medium text-gray-700">
              Notes internes
            </label>
            <textarea id="abonnements-9690"
              className="input-field"
              rows={2}
              value={form.notes}
              onChange={(event) => setField("notes", event.target.value)}
            />
          </div>

          {form.periodicite === "mensuel" && (
            <label className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.afficher_offre_annuelle}
                onChange={(event) =>
                  setField("afficher_offre_annuelle", event.target.checked)
                }
              />
              <span>
                <span className="block font-medium text-blue-900">
                  Afficher l'offre de paiement annuel
                </span>
                <span className="mt-1 block text-blue-800">
                  Le message « Passez au paiement annuel et bénéficiez de 2 mois
                  offerts ! » sera ajouté aux prochaines factures générées.
                </span>
              </span>
            </label>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.actif}
              onChange={(event) => setField("actif", event.target.checked)}
            />
            Abonnement actif
          </label>


          </fieldset>
        </form>
    </Dialog>
  );
};

const Abonnements = () => {
  const [params, setParams] = useSearchParams();
  const filter = params.get("vue") || "actifs";
  const search = params.get("q") || "";
  const setFilter = (value) => { const next = new URLSearchParams(params); next.set("vue", value); setParams(next, { replace: true }); };
  const setSearch = (value) => { const next = new URLSearchParams(params); next.set("q", value); setParams(next, { replace: true }); };
  const [loadError, setLoadError] = useState("");
  const confirm = useConfirm();
  const [actionBusy, runAction] = useActionLock();
  const [abonnements, setAbonnements] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [facturingId, setFacturingId] = useState(null);
  const [facturingAll, setFacturingAll] = useState(false);

  const loadData = useCallback(async () => {
    setLoadError("");
    try {
      const [abonnementsResponse, clientsResponse] = await Promise.all([
        abonnementsAPI.getAll(),
        clientsAPI.getAll(),
      ]);
      setAbonnements(abonnementsResponse.data);
      setClients(clientsResponse.data);
    } catch (error) {
      setLoadError("Impossible de charger les abonnements. Réessayez.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalPeriodesDues = abonnements.reduce(
    (sum, item) => sum + (item.periodes_dues || 0),
    0
  );

  const handleSubmit = (payload) => runAction(async () => {
    try {
      if (editing) {
        await abonnementsAPI.update(editing.id, payload);
        toast.success("Abonnement mis à jour");
      } else {
        await abonnementsAPI.create(payload);
        toast.success("Abonnement créé");
      }
      setShowForm(false);
      setEditing(null);
      await loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Échec de l'enregistrement"
      );
      throw error;
    }
 });

  const handleFacturer = (abonnement) => runAction(async () => {
    const ok = await confirm({ title: "Facturer la prochaine période ?", confirmLabel: "Créer la facture",
      description: "Une facture numérotée sera créée. L'envoi automatique suit vos paramètres de facturation.",
      details: [["Abonnement", abonnement.libelle], ["Client", abonnement.client_nom], ["Montant HT", formatMoney(abonnement.montant_ht, abonnement.client_devise)], ["Échéance", formatDate(abonnement.prochaine_facturation)]] });
    if (!ok) return;

    try {
      setFacturingId(abonnement.id);
      const response = await abonnementsAPI.facturer(abonnement.id);
      toast.success(response.data.message);
      await loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Échec de la facturation"
      );
    } finally {
      setFacturingId(null);
    }
 });

  const handleFacturerTout = () => runAction(async () => {
    const ok = await confirm({
      title: "Facturer toutes les périodes dues ?", confirmLabel: `Créer ${totalPeriodesDues} facture(s)`,
      description: "Chaque période échue génère une facture numérotée. L'envoi automatique suit vos paramètres de facturation.",
      details: abonnements.filter((item) => item.periodes_dues > 0).map((item) => [item.libelle + " / " + item.client_nom, `${item.periodes_dues} période(s), ${formatMoney(item.montant_ht, item.client_devise)} HT par période`]),
    });
    if (!ok) return;

    try {
      setFacturingAll(true);
      const { data } = await abonnementsAPI.facturerEcheances();
      toast.success(data.message);
      (data.erreurs || []).forEach((erreur) =>
        toast.error(`${erreur.libelle} : ${erreur.detail}`)
      );
      await loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Échec de la facturation groupée"
      );
    } finally {
      setFacturingAll(false);
    }
 });

  const handleToggleActif = (abonnement) => runAction(async () => {
    if (!(await confirm({ title: abonnement.actif ? "Mettre l'abonnement en pause ?" : "Réactiver l'abonnement ?", description: abonnement.actif ? "Cet abonnement sera suspendu. Ses factures existantes seront conservées." : "Les périodes dues redeviendront disponibles pour la facturation. Cette action ne crée pas de facture immédiatement.", confirmLabel: abonnement.actif ? "Mettre en pause" : "Réactiver", details: [["Abonnement", abonnement.libelle], ["Client", abonnement.client_nom]] }))) return;
    try {
      await abonnementsAPI.update(abonnement.id, {
        client_id: abonnement.client_id,
        libelle: abonnement.libelle,
        description: abonnement.description,
        montant_ht: abonnement.montant_ht,
        unite: abonnement.unite,
        periodicite: abonnement.periodicite,
        date_debut: abonnement.date_debut,
        date_fin: abonnement.date_fin,
        actif: !abonnement.actif,
        notes: abonnement.notes,
        afficher_offre_annuelle: abonnement.afficher_offre_annuelle || false,
      });
      toast.success(abonnement.actif ? "Abonnement mis en pause" : "Abonnement réactivé");
      await loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de la mise à jour");
    }
 });

  const handleDelete = (abonnement) => runAction(async () => {
    const ok = await confirm({ title: "Supprimer cet abonnement ?", description: "L'abonnement sera supprimé définitivement. Les factures déjà créées resteront dans l'historique.", confirmLabel: "Supprimer l'abonnement", danger: true, details: [["Abonnement", abonnement.libelle], ["Client", abonnement.client_nom]] });
    if (!ok) return;

    try {
      await abonnementsAPI.delete(abonnement.id);
      toast.success("Abonnement supprimé");
      await loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Échec de la suppression"
      );
    }
 });

  const filteredSubscriptions = abonnements.filter((item) => (filter === "tous" || Boolean(item.archived) === (filter === "archives")) && (!params.get("client") || params.get("client") === item.client_id) && `${item.libelle} ${item.client_nom}`.toLocaleLowerCase("fr").includes(search.trim().toLocaleLowerCase("fr")));
  const archiveSubscription = (item) => runAction(async () => {
    if (!await confirm({ title: item.archived ? "Restaurer l'abonnement ?" : "Archiver l'abonnement ?", confirmLabel: item.archived ? "Restaurer en pause" : "Archiver", details: [["Abonnement", item.libelle], ["Client", item.client_nom]], description: item.archived ? "L'abonnement reviendra dans la liste, en pause. Réactivez-le séparément pour reprendre la facturation." : "L'abonnement sera retiré des périodes à facturer. Son historique et ses factures restent consultables." })) return;
    try { await abonnementsAPI[item.archived ? "restore" : "archive"](item.id); await loadData(); }
    catch (error) { toast.error(error.response?.data?.detail || "Impossible de modifier l'archivage"); }
  });

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Abonnements récurrents
          </h1>
          <p className="text-gray-600">
            Maintenance, hébergement, forfaits… générez les factures d'un clic.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
        >
          <Plus className="h-5 w-5" />
          <span>Nouvel abonnement</span>
        </button>
      </div>

      {totalPeriodesDues > 0 && (
        <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-900">
                  {totalPeriodesDues} période(s) à facturer
                </p>
                <p className="text-sm text-amber-800">
                  Des échéances sont dépassées et n'ont pas encore de facture.
                </p>
              </div>
            </div>
            <button
              onClick={handleFacturerTout}
              className="btn-primary sm:w-auto"
              disabled={facturingAll || actionBusy}
            >
              {facturingAll ? "Génération…" : "Tout facturer"}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <LoadError message={loadError} onRetry={loadData} />
      <div className="flex flex-wrap gap-2">{[["actifs", "Courants"], ["archives", "Archivés"], ["tous", "Tous"]].map(([key, label]) => <button type="button" key={key} className="btn-secondary" aria-pressed={filter === key} onClick={() => setFilter(key)}>{label}</button>)}</div>
      <label className="block text-sm">Rechercher un abonnement<input className="input-field mt-1" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
      {params.get("client") && <button className="btn-secondary" onClick={() => { const next = new URLSearchParams(params); next.delete("client"); setParams(next); }}>Tous les clients</button>}
      {filteredSubscriptions.length === 0 && abonnements.length > 0 && !loadError && <p role="status" className="card">Aucun abonnement ne correspond à ces filtres.</p>}
      {abonnements.length === 0 && !loadError ? (
          <div className="py-12 text-center">
            <RefreshCw className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Aucun abonnement
            </h3>
            <p className="mb-4 text-gray-600">
              Créez un abonnement pour facturer automatiquement une prestation
              récurrente.
            </p>
            <button
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
              className="btn-primary"
            >
              Créer un abonnement
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {filteredSubscriptions.map((abonnement) => {
              const du = (abonnement.periodes_dues || 0) > 0;
              return (
                <div
                  key={abonnement.id}
                  className={`rounded-lg border p-5 transition-shadow hover:shadow-md ${
                    du ? "border-amber-300 bg-amber-50/40" : "border-gray-200"
                  }`}
                >
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-gray-900">
                        {abonnement.libelle}
                      </h3>
                      <p className="flex items-center gap-1 text-sm text-gray-600">
                        <User className="h-4 w-4" />
                        {abonnement.client_nom || "Client supprimé"}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-1">
                      <button
                        onClick={() => handleFacturer(abonnement)}
                        className="p-2 min-h-[44px] min-w-[44px] text-green-600 hover:text-green-800 disabled:opacity-40"
                        title={
                          du
                            ? "Générer la facture de la prochaine période"
                            : "Aucune période échue à facturer"
                        }
                        disabled={actionBusy ||
                          !du ||
                          !abonnement.actif ||
                          abonnement.facturation_a_confirmer ||
                          facturingId === abonnement.id
                        }
                      >
                        {facturingId === abonnement.id ? (
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
                        ) : (
                          <Receipt className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleActif(abonnement)} disabled={actionBusy || abonnement.archived || clients.find((client) => client.id === abonnement.client_id)?.archived}
                        className="p-2 min-h-[44px] min-w-[44px] text-gray-600 hover:text-gray-900"
                        title={
                          abonnement.actif
                            ? "Mettre en pause"
                            : "Réactiver"
                        }
                      >
                        {abonnement.actif ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        disabled={abonnement.archived}
                        onClick={() => {
                          setEditing(abonnement);
                          setShowForm(true);
                        }}
                        className="p-2 min-h-[44px] min-w-[44px] text-blue-600 hover:text-blue-900"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        disabled={actionBusy} onClick={() => (abonnement.factures?.length || abonnement.periodes_facturees > 0 || abonnement.archived) ? archiveSubscription(abonnement) : handleDelete(abonnement)}
                        className="p-2 min-h-[44px] min-w-[44px] text-red-600 hover:text-red-900"
                        title={abonnement.archived ? "Restaurer" : (abonnement.factures?.length || abonnement.periodes_facturees > 0) ? "Archiver" : "Supprimer"}
                      >
                        {abonnement.archived ? <RotateCcw className="h-4 w-4" /> : (abonnement.factures?.length || abonnement.periodes_facturees > 0) ? <Archive className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <details className="mb-4 rounded-lg border border-gray-100 p-3" open={abonnement.facturation_a_confirmer || undefined}>
                    <summary className="cursor-pointer text-sm font-medium">{abonnement.facturation_a_confirmer ? "Conditions à confirmer" : "Conditions de facturation"}</summary>
                    <div className="mt-3"><SourceBillingConditions kind="abonnements" sourceId={abonnement.id} onConfirmed={loadData} /></div>
                  </details>
                  {abonnement.description && (
                    <p className="mb-3 text-sm text-gray-600">
                      {abonnement.description}
                    </p>
                  )}

                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="rounded bg-gray-100 px-2 py-1 text-sm font-semibold text-gray-900">
                      {formatMoney(abonnement.montant_ht, abonnement.client_devise)}
                    </span>
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                      {abonnement.periodicite_label}
                    </span>
                    {abonnement.archived && <span className="rounded bg-gray-200 px-2 py-1 text-xs">Archivé</span>}
                    {!abonnement.actif && !abonnement.archived && (
                      <span className="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700">
                        En pause
                      </span>
                    )}
                    {abonnement.termine && (
                      <span className="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700">
                        Terminé
                      </span>
                    )}
                    {du && (
                      <span className="rounded bg-amber-200 px-2 py-1 text-xs font-medium text-amber-900">
                        {abonnement.periodes_dues} période(s) à facturer
                      </span>
                    )}
                    {abonnement.afficher_offre_annuelle &&
                      abonnement.periodicite === "mensuel" && (
                        <span className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                          Offre annuelle : 2 mois offerts
                        </span>
                      )}
                  </div>

                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>
                        Prochaine échéance :{" "}
                        <span className="font-medium text-gray-900">
                          {formatDate(abonnement.prochaine_facturation)}
                        </span>
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Depuis le {formatDate(abonnement.date_debut)}
                      {abonnement.date_fin
                        ? ` jusqu'au ${formatDate(abonnement.date_fin)}`
                        : " - sans date de fin"}
                      {" · "}
                      {abonnement.periodes_facturees || 0} période(s) facturée(s)
                    </div>
                  </div>

                  {abonnement.factures?.length > 0 && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                        Historique des factures
                      </summary>
                      <ul className="mt-2 space-y-1 text-xs text-gray-600">
                        {abonnement.factures
                          .slice()
                          .reverse()
                          .map((entry) => (
                            <li
                              key={entry.facture_id}
                              className="flex flex-wrap items-center gap-2"
                            >
                              <span className="font-medium text-gray-900">
                                <Link className="text-primary-700 hover:underline" to={`/factures?fiche=${entry.facture_id}`}>{entry.facture_numero}</Link>
                              </span>
                              <span>
                                {formatDate(entry.periode_debut)} →{" "}
                                {formatDate(entry.periode_fin)}
                              </span>
                              {entry.statut && (
                                <span
                                  className={`rounded px-1.5 py-0.5 ${
                                    entry.statut === "paye"
                                      ? "bg-green-100 text-green-800"
                                      : entry.statut === "annulee"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-yellow-100 text-yellow-800"
                                  }`}
                                >
                                  {entry.statut === "paye"
                                    ? "Payé"
                                    : entry.statut === "annulee"
                                    ? "Annulée"
                                    : "En attente"}
                                </span>
                              )}
                            </li>
                          ))}
                      </ul>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <AbonnementForm
          abonnement={editing}
          clients={clients.filter((client) => !client.archived || client.id === editing?.client_id)}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

export default Abonnements;
